import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./NewEvent.css";
import { useNavigate } from "react-router-dom";
import { clearAuth, getAuthToken } from "../../../../utils/auth";

const NewEvent = ({ id, onClose, refreshData }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date_start: "",
    date_end: "",
    image: null,
    full_path: "",
  });
  const token = getAuthToken();
  const navigate = useNavigate();
  const fetched = useRef(false);
  const [mensagem, setMensagem] = useState(null);
  const [severidade, setSeveridade] = useState("info");

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // Se for um upload de arquivo
    if (name === "image" && files && files[0]) {
      const file = files[0];
      const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
      const maxSize = 8 * 1024 * 1024;

      if (!allowedTypes.includes(file.type) || file.size > maxSize) {
        setMensagem("Envie uma imagem JPG, PNG ou WEBP de até 8 MB.");
        setSeveridade("error");
        e.target.value = "";
        return;
      }

      setMensagem(null);
      setFormData({
        ...formData,
        image: file,
        full_path: URL.createObjectURL(file), // Cria uma URL para preview
      });
    } else {
      // Para outros campos de texto
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [date] = dateString.split(" ");
    return date;
  };

  useEffect(() => {
    if (id && !fetched.current) {
      fetched.current = true;

      fetch(`https://www.euvoup14sports.com.br/api/event/${id}`)
        .then((response) => {
          return response.json();
        })
        .then(({ error, message, data }) => {
          if (error) {
            console.error("Erro da API:", message);
            return;
          }
          setFormData({
            title: data.title || "",
            description: data.description || "",
            date_start: formatDateForDisplay(data.date_start) || "",
            date_end: formatDateForDisplay(data.date_end) || "",
            full_path: data.full_path || "", // Adicionei o caminho da imagem
            image: null, // A imagem real não vem na consulta, apenas o caminho
          });
        })
        .catch((error) => {
          if (
            error.response?.data?.message === "Unauthenticated." ||
            error.message === "Unauthenticated" ||
            error.response?.status === 401
          ) {
            handleLogout();
            clearAuth();
            navigate("/login", {
              state: {
                message: "Sua sessão expirou. Por favor, faça login novamente.",
              },
            });
          } else {
            // Handle other types of errors
            console.error("Erro de autenticação:", error);
          }
        });
    }
  }, [id]);

  const handleLogout = async () => {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
    try {
      await axios.post(
        `https://www.euvoup14sports.com.br/api/logout`,
        {},
        config
      );
    } catch (error) {
      console.error("Erro ao salvar evento", error);
    }
    clearAuth();
  };

  const handleSalvar = async (event) => {
    event.preventDefault(); // Previne o comportamento padrão do formulário

    try {
      const dataToSend = new FormData();
      dataToSend.append("title", formData.title);
      dataToSend.append("description", formData.description);
      dataToSend.append("dateStart", formData.date_start + " 00:00:00");
      dataToSend.append("dateEnd", formData.date_end + " 00:00:00");

      if (formData.image instanceof File) {
        dataToSend.append("image", formData.image);
      }

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      };

      if (id) {
        await axios.post(
          `https://www.euvoup14sports.com.br/api/event/${id}`,
          dataToSend,
          config
        );
      } else {
        await axios.post(
          "https://www.euvoup14sports.com.br/api/event",
          dataToSend,
          config
        );
      }
      setMensagem("Evento salvo com sucesso!");
      setSeveridade("success");

      // Chama onClose com true para atualizar os dados no Admin.js
      if (refreshData) {
        refreshData(); // Chama a função passada como prop
      }
      onClose(true);
    } catch (error) {
      if (
        error.response?.data?.message === "Unauthenticated." ||
        error.message === "Unauthenticated" ||
        error.response?.status === 401
      ) {
        handleLogout();
        clearAuth();
        navigate("/login", {
          state: {
            message: "Sua sessão expirou. Por favor, faça login novamente.",
          },
        });
      } else {
        // Handle other types of errors
        console.error("Erro de autenticação:", error);
      }
      setMensagem(
        `Erro ao salvar evento: ${
          error.response?.data?.message || "Erro no servidor"
        }`
      );
      setSeveridade("error");
    }
  };

  return (
    <div className="event-form-overlay">
      <section
        className="event-form-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-form-title"
      >
        <header className="event-form-header">
          <div>
            <span>{id ? "Edição" : "Cadastro"}</span>
            <h2 id="event-form-title">{id ? "Editar evento" : "Novo evento"}</h2>
            <p>Preencha as informações que serão exibidas na página pública.</p>
          </div>
          <button
            type="button"
            className="event-form-close"
            onClick={() => onClose(false)}
            aria-label="Fechar formulário"
          >
            ×
          </button>
        </header>

        <form
          className="event-form"
          onSubmit={handleSalvar}
          encType="multipart/form-data"
        >
          <div className="event-form-body">
            <div className="event-form-field event-form-field-full">
              <label htmlFor="title">Título do evento</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Ex.: Ultra Rota Tamarana"
                maxLength={120}
                required
              />
            </div>

            <div className="event-form-field event-form-field-full">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                placeholder="Conte brevemente o que torna este evento especial."
                maxLength={1000}
              />
            </div>

            <div className="event-form-field">
              <label htmlFor="date_start">Data de início</label>
              <input
                id="date_start"
                type="date"
                name="date_start"
                value={formData.date_start || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-field">
              <label htmlFor="date_end">Data de término</label>
              <input
                id="date_end"
                type="date"
                name="date_end"
                value={formData.date_end || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="event-form-field event-form-field-full">
              <label htmlFor="event-image">Imagem do evento</label>
              <div className="event-image-upload">
                <input
                  id="event-image"
                  type="file"
                  name="image"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleChange}
                  required={!id}
                />
                <small>Use uma imagem quadrada em PNG, JPG ou WEBP.</small>
              </div>
            </div>

            {formData.full_path && (
              <div className="event-image-preview event-form-field-full">
                <img src={formData.full_path} alt="Prévia do evento" />
                <span>Prévia da imagem</span>
              </div>
            )}

            {mensagem && (
              <div className={`event-form-alert event-form-alert-${severidade}`} role="status">
                {mensagem}
              </div>
            )}
          </div>

          <footer className="event-form-actions">
            <button
              type="button"
              className="event-form-cancel"
              onClick={() => onClose(false)}
            >
              Cancelar
            </button>
            <button type="submit" className="event-form-save">
              {id ? "Salvar alterações" : "Criar evento"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};

export default NewEvent;
